// TIP-611 — dial-code picker plus number, used by `Mobile / WhatsApp` and by
// `Contact mobile` in the reference panel. 45 codes, defaulting to +971.
//
// The picker shares the combobox's viewport-coordinate panel, and its portal,
// for the same two reasons: the form body scrolls, so a panel in the flow would
// be clipped; and `.cb-modal` carries an animation that mentions `transform`,
// which makes it a containing block for fixed descendants, so a panel left in
// the tree opens shifted right and down by the modal's own offset.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./counterpartyBriefs.css";
import { CbLabel } from "./CbField";

// Per-country grouping, so a UAE number reads 50 244 8190 rather than as ten
// unbroken digits. A code with no mask is left exactly as typed.
const MASKS = {
  "+971": "XX XXX XXXX",
  "+966": "XX XXX XXXX",
  "+974": "XXXX XXXX",
  "+965": "XXXX XXXX",
  "+973": "XXXX XXXX",
  "+968": "XXXX XXXX",
  "+91": "XXXXX XXXXX",
  "+44": "XXXX XXXXXX",
  "+1": "(XXX) XXX-XXXX",
};

export const formatPhone = (raw, dial) => {
  const mask = MASKS[dial];
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (!mask || !digits) return digits;
  let out = "";
  let i = 0;
  for (const ch of mask) {
    if (i >= digits.length) break;
    out += ch === "X" ? digits[i++] : ch;
  }
  return out;
};

// The design's placeholder is the mask with every X as a zero, so `00 000 0000`
// for the UAE — which is the placeholder the spec quotes.
export const phonePlaceholder = (dial) => (MASKS[dial] || "").replace(/X/g, "0") || "Phone number";

const CbDialPhone = ({
  error,
  id,
  label,
  required,
  dial,
  phone,
  options = [],
  onDialChange,
  onPhoneChange,
  testId,
  // TIP-611 — the number and the code lock independently: the reference
  // lookup answers with a number, and only sometimes with a country code in
  // front of it. Locking the picker on a bare national number would leave the
  // customer unable to correct a code the lookup never supplied.
  disabled = false,
  disabledDial = false,
}) => {
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [panel, setPanel] = useState({ top: 0, left: 0, height: 280 });

  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    const flip = below < 180 && above > below;
    const height = Math.max(160, Math.min(280, flip ? above : below));
    setPanel({
      top: flip ? Math.max(8, r.top - height - 5) : r.bottom + 5,
      // Held inside both edges: the 300px panel is wider than its 118px
      // trigger, so it would otherwise run off the right on a narrow window.
      left: Math.max(8, Math.min(r.left, window.innerWidth - 316)),
      height,
    });
  }, []);

  // Measured before paint, so the panel never shows for a frame at the
  // previous field's coordinates.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      // Portalled out of the field, so the panel is not inside rootRef. Without
      // this second test a mousedown in the search box reads as a click
      // outside and closes the panel before a character is typed.
      const inTrigger = rootRef.current && rootRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    const onMove = () => place();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, place]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      `${o.label} ${o.value} ${o.sub || ""}`.toLowerCase().includes(q)
    );
  }, [options, query]);

  // Several countries share a dial code (+1 is the United States and Canada),
  // so the first match is the one whose flag is drawn.
  const selected = options.find((o) => o.value === dial) || null;

  return (
    <div
      className={`cb-field${error ? " cb-field--error" : ""}${
        disabled && disabledDial ? " cb-field--disabled" : ""
      }`}
      ref={rootRef}
    >
      <CbLabel htmlFor={id} label={label} required={required} />
      <div className="cb-dial-row">
        <button
          type="button"
          ref={btnRef}
          data-testid={testId && `${testId}-dial`}
          className="cb-dial-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`${label} dial code`}
          disabled={disabledDial}
          onClick={() => {
            // Belt and braces alongside the attribute, matching the combobox.
            if (disabledDial) return;
            if (open) {
              setOpen(false);
              return;
            }
            place();
            setQuery("");
            setOpen(true);
          }}
        >
          <span className="cb-dial-selected">
            {selected && selected.leading && (
              <span className="cb-combo-flag" aria-hidden="true">
                {selected.leading}
              </span>
            )}
            <span className="cb-dial-code">{dial}</span>
          </span>
          <svg className="cb-combo-chevron" width="13" height="9" viewBox="0 0 12 8" fill="none" aria-hidden="true">
            <path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <input
          id={id}
          type="text"
          inputMode="tel"
          className={`cb-input${error ? " cb-input--error" : ""}`}
          aria-invalid={error ? "true" : undefined}
          data-testid={testId}
          value={phone}
          required={required}
          disabled={disabled}
          placeholder={phonePlaceholder(dial)}
          onChange={(e) => onPhoneChange && onPhoneChange(formatPhone(e.target.value, dial))}
        />

        {open && createPortal(
          <div
            ref={panelRef}
            className="cb-combo-panel cb-dial-panel"
            style={{ top: panel.top, left: panel.left, maxHeight: panel.height }}
          >
            <div className="cb-combo-search">
              {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
              <input
                autoFocus
                type="text"
                className="cb-combo-search-input"
                value={query}
                placeholder="Search code or country"
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="cb-combo-list" role="listbox" aria-label={`${label} dial code`}>
              {filtered.map((o) => (
                <div
                  key={`${o.label}-${o.value}`}
                  role="option"
                  aria-selected={o.value === dial}
                  className={`cb-combo-option${o.value === dial ? " is-selected" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onDialChange && onDialChange(o.value);
                    setOpen(false);
                  }}
                >
                  {o.leading && (
                    <span className="cb-combo-flag" aria-hidden="true">
                      {o.leading}
                    </span>
                  )}
                  <span className="cb-combo-option-label">{o.label}</span>
                  <span className="cb-combo-option-sub">{o.sub || o.value}</span>
                </div>
              ))}
              {filtered.length === 0 && <div className="cb-combo-empty">No matches</div>}
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* TIP-611 — the backend's own message about this field. */}
      {error && <p className="cb-field-error">{error}</p>}
    </div>
  );
};

export default CbDialPhone;
